import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import { Layout } from './components/Layout';
import Auth from './pages/Auth/Auth';
import Vendors from './pages/Vendors/Vendors';
import VendorProfile from './pages/Vendors/VendorProfile';
import VendorProducts from './pages/Vendors/VendorProducts';
import VendorOrders from './pages/Vendors/VendorOrders';
import VendorTransactions from './pages/Vendors/VendorTransactions';
import AdminTransactions from './pages/Transactions/AdminTransactions';
import PayoutRequestDetail from './pages/Transactions/PayoutRequestDetail';
import Applications from './pages/Applications/Applications';
import Admins from './pages/Admins/Admins';
import OTPs from './pages/OTPs/OTPs';
import Promotions from './pages/Promotions/Promotions';
import Catalogue from './pages/Catalogue/Catalogue';
import ProfileDetail from './pages/Catalogue/ProfileDetail';
import ServiceDetail from './pages/Catalogue/ServiceDetail';
import Discounts from './pages/Discounts/Discounts';
import Marketing from './pages/Marketing/Marketing';
import Orders from './pages/Orders/Orders';
import OrderDetail from './pages/Orders/OrderDetail';
import Categories from './pages/Categories/Categories';
import CategoryDetails from './pages/Categories/CategoryDetails';
import Cities from './pages/Cities/Cities';
import Notifications from './pages/Notifications/Notifications';
import Consultation from './pages/Consultation/Consultation';
import ReportedProblems from './pages/ReportedProblems/ReportedProblems';

// Hooks
import { useAuth } from './hooks/useAuth';

// Services (notifications service no longer needed at app level)

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#67314F', // --palette-light-brand-primary-value-hex
    },
    secondary: {
      main: '#AC654D', // --palette-light-brand-secondary-value-hex
    },
    error: {
      main: '#EF4444', // --palette-light-status-danger-value-hex
    },
    warning: {
      main: '#ECBE96', // --palette-light-status-warning-value-hex
    },
    success: {
      main: '#426A53', // --palette-light-status-success-value-hex
    },
    text: {
      primary: '#181818', // --palette-light-text-primary-value-hex
      secondary: '#93989C', // --palette-light-text-secondary-value-hex
    },
    background: {
      default: '#FFFFFF', // --palette-light-background-primary-value-hex
      paper: '#F6F8F8', // --palette-light-background-secondary-value-hex
    },
  },
  typography: {
    fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    // Ensure Material-UI doesn't override our custom styles
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          '&.Mui-selected': {
            // Ensure selected tab uses our custom colors
          },
        },
      },
    },
  },
});

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  const shouldShowAuthenticatedRoutes = isAuthenticated;

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        Loading...
      </Box>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
          <Box sx={{ display: 'flex' }}>
            <Routes>
              {/* Authentication Routes - No Layout/Sidebar */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              
              {/* Default Route - Always redirect to auth first */}
              <Route path="/" element={<Navigate to="/auth" replace />} />
              
              {/* Protected Routes - With Layout/Sidebar */}
              {shouldShowAuthenticatedRoutes ? (
                <>
                  <Route path="/vendors" element={<Layout><Vendors /></Layout>} />
                  <Route path="/vendors/:id" element={<Layout><VendorProfile /></Layout>} />
                  <Route path="/vendors/:id/products" element={<Layout><VendorProducts /></Layout>} />
                  <Route path="/vendors/:id/orders" element={<Layout><VendorOrders /></Layout>} />
                  <Route path="/vendors/:id/transactions" element={<Layout><VendorTransactions /></Layout>} />
                  <Route path="/transactions" element={<Layout><AdminTransactions /></Layout>} />
                  <Route path="/transactions/payouts/:id" element={<Layout><PayoutRequestDetail /></Layout>} />
                  <Route path="/applications" element={<Layout><Applications /></Layout>}/>
                  <Route path="/admins" element={<Layout><Admins /></Layout>}/>
                  <Route path="/otps" element={<Layout><OTPs /></Layout>}/>
                  <Route path="/promotions" element={<Layout><Promotions /></Layout>}/>
                  <Route path="/discounts" element={<Layout><Discounts /></Layout>}/>
                  <Route path="/marketing" element={<Layout><Marketing /></Layout>}/>
                  <Route path="/catalogue" element={<Layout><Catalogue /></Layout>}/>
                  <Route path="/catalogue/profiles/:id" element={<Layout><ProfileDetail /></Layout>}/>
                  <Route path="/catalogue/services/:id" element={<Layout><ServiceDetail /></Layout>}/>
                  <Route path="/categories" element={<Layout><Categories /></Layout>}/>
                  <Route path="/categories/:categoryId" element={<Layout><CategoryDetails /></Layout>}/>
                  <Route path="/cities" element={<Layout><Cities /></Layout>}/>
                  <Route path="/orders" element={<Layout><Orders /></Layout>}/>
                  <Route path="/orders/:id" element={<Layout><OrderDetail /></Layout>}/>
                  <Route path="/notifications" element={<Layout><Notifications /></Layout>}/>
                  <Route path="/reported-problems" element={<Layout><ReportedProblems /></Layout>} />
                  <Route path="/event-planner" element={<Layout><Consultation /></Layout>} />
                  <Route path="/services"  />
                  <Route path="/reviews"  />
                  <Route path="/settings"  />
                  <Route path="/dashboard" element={<Layout><Admins /></Layout>} />
                </>
              ) : (
                <Route path="*" element={<Navigate to="/auth" replace />} />
              )}
            </Routes>
          </Box>
        </Router>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </ThemeProvider>
      </LocalizationProvider>
    </QueryClientProvider>
  );
}

export default App;